import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CssBaseline from '@mui/material/CssBaseline';
import SearchIcon from '@mui/icons-material/Search';
import ForumIcon from '@mui/icons-material/Forum';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PushPinIcon from '@mui/icons-material/PushPin';
import ChatIcon from '@mui/icons-material/Chat';
import WarningIcon from '@mui/icons-material/Warning';
import ScienceIcon from '@mui/icons-material/Science';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WaterIcon from '@mui/icons-material/Water';
import SensorsIcon from '@mui/icons-material/Sensors';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import PhotoIcon from '@mui/icons-material/Photo';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import { CommunityProvider, useCommunity } from '../context/CommunityContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Icon mapping for topics
const topicIcons = {
  chat: ChatIcon,
  warning: WarningIcon,
  science: ScienceIcon,
  lightbulb: LightbulbIcon,
  water: WaterIcon,
  sensors: SensorsIcon,
  agriculture: AgricultureIcon,
  photo: PhotoIcon,
};

// Format relative time
function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function TopicCard({ topic, isSelected, onClick }) {
  const IconComponent = topicIcons[topic.icon] || ChatIcon;

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        borderColor: isSelected ? `${topic.color}.main` : 'divider',
        bgcolor: isSelected ? `${topic.color}.50` : 'background.paper',
        '&:hover': {
          borderColor: `${topic.color}.main`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconComponent sx={{ color: `${topic.color}.main`, fontSize: 24 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
            {topic.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
            {topic.description}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}

function PostCard({ post, onClick }) {
  const topic = useCommunity().forumTopics.find(t => t.id === post.topicId);
  const TopicIcon = topicIcons[topic?.icon] || ChatIcon;

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
    >
      <Stack spacing={1.5}>
        {/* Header with pins and solved badge */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {post.isPinned && (
            <Chip
              icon={<PushPinIcon sx={{ fontSize: 14 }} />}
              label="Pinned"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22 }}
            />
          )}
          {post.isSolved && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label="Solved"
              size="small"
              color="success"
              sx={{ height: 22 }}
            />
          )}
          {topic && (
            <Chip
              icon={<TopicIcon sx={{ fontSize: 14 }} />}
              label={topic.name}
              size="small"
              variant="outlined"
              sx={{ height: 22 }}
            />
          )}
        </Stack>

        {/* Title */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
          {post.title}
        </Typography>

        {/* Content preview */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {post.content.replace(/[#*`\n]/g, ' ').substring(0, 200)}
        </Typography>

        {/* Tags */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {post.tags.slice(0, 4).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          ))}
        </Stack>

        {/* Footer stats */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            by <strong>{post.author}</strong>
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatRelativeTime(post.createdAt)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <VisibilityIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {post.views}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {post.replyCount}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ThumbUpOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {post.likes}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}

function ForumContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    forumPosts,
    forumTopics,
    canPost,
    isDemo,
    loading,
    searchPosts,
    getPostsByTopic,
    getTrendingPosts,
    getRecentPosts,
    getPinnedPosts,
  } = useCommunity();

  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('q') || '');
  const [selectedTopic, setSelectedTopic] = React.useState(searchParams.get('topic') || 'all');
  const [activeTab, setActiveTab] = React.useState(0);

  const basePath = location.pathname.startsWith('/demo') ? '/demo' : '/app';

  // Get filtered posts based on current view
  const getDisplayPosts = () => {
    if (searchQuery) {
      return searchPosts(searchQuery, selectedTopic !== 'all' ? selectedTopic : null);
    }

    switch (activeTab) {
      case 0: // All / Recent
        return selectedTopic === 'all' ? getRecentPosts(50) : getPostsByTopic(selectedTopic);
      case 1: // Trending
        return getTrendingPosts(20);
      default:
        return forumPosts;
    }
  };

  const displayPosts = getDisplayPosts();
  const pinnedPosts = getPinnedPosts();

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery) {
      setSearchParams({ q: searchQuery, topic: selectedTopic });
    } else {
      searchParams.delete('q');
      setSearchParams(searchParams);
    }
  };

  // Handle topic selection
  const handleTopicSelect = (topicId) => {
    setSelectedTopic(topicId);
    setSearchParams(prev => {
      if (topicId === 'all') {
        prev.delete('topic');
      } else {
        prev.set('topic', topicId);
      }
      return prev;
    });
  };

  // Navigate to post
  const handlePostClick = (postId) => {
    navigate(`${basePath}/forum/post/${postId}`);
  };

  // Navigate to create post
  const handleCreatePost = () => {
    navigate(`${basePath}/forum/new`);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1400px' }, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ForumIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
              Community Forum
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreatePost}
            disabled={!canPost}
            sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif' }}
          >
            New Post
          </Button>
        </Stack>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Ask questions, share experiences, and connect with fellow growers
        </Typography>
      </Stack>

      {/* Demo mode notice */}
      {isDemo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You're viewing the forum in demo mode. Sign up to post questions and reply to discussions!
        </Alert>
      )}

      {/* Search and Filters */}
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <form onSubmit={handleSearch}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            <TextField
              placeholder="Search posts, topics, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="outlined" sx={{ minWidth: 100 }}>
              Search
            </Button>
          </Stack>
        </form>
      </Card>

      <Grid container spacing={3}>
        {/* Topics Sidebar */}
        <Grid item xs={12} md={3}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Topics
          </Typography>
          <Stack spacing={1}>
            <Card
              variant="outlined"
              onClick={() => handleTopicSelect('all')}
              sx={{
                p: 2,
                cursor: 'pointer',
                borderColor: selectedTopic === 'all' ? 'primary.main' : 'divider',
                bgcolor: selectedTopic === 'all' ? 'primary.50' : 'background.paper',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ForumIcon sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  All Topics
                </Typography>
              </Stack>
            </Card>
            {forumTopics.map((topic) => (
              <TopicCard
                key={topic.id}
                topic={topic}
                isSelected={selectedTopic === topic.id}
                onClick={() => handleTopicSelect(topic.id)}
              />
            ))}
          </Stack>
        </Grid>

        {/* Posts List */}
        <Grid item xs={12} md={9}>
          {/* Tabs for filtering */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Recent"
              sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif' }}
            />
            <Tab
              icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Trending"
              sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif' }}
            />
          </Tabs>

          {/* Pinned Posts */}
          {activeTab === 0 && selectedTopic === 'all' && pinnedPosts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontFamily: 'Inter, sans-serif' }}>
                Pinned
              </Typography>
              <Stack spacing={1.5}>
                {pinnedPosts.map((post) => (
                  <PostCard key={post.id} post={post} onClick={() => handlePostClick(post.id)} />
                ))}
              </Stack>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* Search Results Header */}
          {searchQuery && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {displayPosts.length} result{displayPosts.length !== 1 ? 's' : ''} for "{searchQuery}"
              {selectedTopic !== 'all' && ` in ${forumTopics.find(t => t.id === selectedTopic)?.name}`}
            </Typography>
          )}

          {/* Posts */}
          {loading ? (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              Loading posts...
            </Typography>
          ) : displayPosts.length === 0 ? (
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary' }}>
                {searchQuery ? 'No posts found matching your search.' : 'No posts in this topic yet.'}
              </Typography>
              {canPost && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreatePost}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Be the first to post!
                </Button>
              )}
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {displayPosts
                .filter(p => !p.isPinned || activeTab !== 0 || selectedTopic !== 'all')
                .map((post) => (
                  <PostCard key={post.id} post={post} onClick={() => handlePostClick(post.id)} />
                ))}
            </Stack>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default function ForumPage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <CommunityProvider>
              <SideMenu />
              <AppNavbar />
              <Box
                component="main"
                sx={(theme) => ({
                  flexGrow: 1,
                  backgroundColor: theme.vars
                    ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                    : alpha(theme.palette.background.default, 1),
                  overflow: 'auto',
                  minHeight: '100vh',
                })}
              >
                <Stack
                  sx={{
                    pt: { xs: 10, md: 3 },
                    pb: 5,
                  }}
                >
                  <ForumContent />
                </Stack>
              </Box>
            </CommunityProvider>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
